import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTags } from '../hooks/useTags';
import { auth } from '../firebase';
import { 
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors 
} from '@dnd-kit/core';
import { 
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove 
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, Palmtree, GripVertical, Plus, Trash2, ChevronDown, ChevronRight, Edit3, FolderInput } from 'lucide-react';

function SortableTagItem({ tag, dict, onToggleSpecial, onRemove, onAddSubTag, onEdit, onMove, depth = 0 }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isRoot = tag.isRoot;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: tag.id,
    disabled: isRoot
  });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : 1,
    opacity: isDragging ? 0.5 : 1,
  };

  const children = Object.values(dict)
    .filter(t => t.parentId === tag.id)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div ref={setNodeRef} style={style} className={`flex flex-col ${depth > 0 ? 'ml-6 border-l-[3px] border-zinc-300 pl-4 mt-2' : 'mt-2'}`}>
      <div className={`flex items-center gap-3 bg-white p-3 rounded-xl border ${isRoot ? 'border-zinc-400 shadow-sm bg-zinc-50/50' : 'border-zinc-200'}`}>
        {!isRoot && (
          <div {...attributes} {...listeners} className="cursor-grab text-zinc-400 hover:text-zinc-800 transition-colors">
            <GripVertical size={16} />
          </div>
        )}
        <span className={`font-black ${isRoot ? 'text-zinc-900 text-[13px] uppercase tracking-wider' : 'text-zinc-800 text-xs'}`}>{tag.name}</span>
        
        <div className="flex-1" />
        
        {!isRoot && (
          <button 
            onClick={() => onToggleSpecial(tag.id)}
            className={`p-1.5 rounded-lg transition-colors border ${tag.isSpecial ? 'bg-orange-100 text-orange-600 border-orange-200' : 'bg-zinc-50 text-zinc-400 border-zinc-200 hover:text-orange-500 hover:border-orange-200'}`}
            title="Toggle Special (Palm Tree)"
          >
            <Palmtree size={14} />
          </button>
        )}
        
        <button onClick={() => onMove(tag)} className="p-1.5 text-zinc-400 hover:text-purple-600 transition-colors" title="Move Tag">
          <FolderInput size={14} />
        </button>

        <button onClick={() => onEdit(tag)} className="p-1.5 text-zinc-400 hover:text-blue-600 transition-colors" title="Edit Tag">
          <Edit3 size={14} />
        </button>

        <button onClick={() => onRemove(tag.id)} className="p-1.5 text-zinc-400 hover:text-red-500 transition-colors" title="Delete Tag">
          <Trash2 size={14} />
        </button>
        
        <button 
          onClick={() => {
            const name = window.prompt(`Add new tag under ${tag.name}:`);
            if (name && name.trim()) {
              onAddSubTag(tag.id, name.trim());
              setIsExpanded(true);
            }
          }}
          className="p-1.5 text-zinc-400 hover:text-blue-600 transition-colors"
          title="Add Sub-Tag"
        >
          <Plus size={14} strokeWidth={2.5} />
        </button>

        {children.length > 0 && (
          <button onClick={() => setIsExpanded(!isExpanded)} className="p-1.5 text-zinc-400">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        )}
      </div>

      {isExpanded && children.length > 0 && (
        <SortableContext items={children.map(c => c.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-1">
            {children.map(child => (
              <SortableTagItem 
                key={child.id} 
                tag={child} 
                dict={dict} 
                onToggleSpecial={onToggleSpecial} 
                onRemove={onRemove} 
                onAddSubTag={onAddSubTag} 
                onEdit={onEdit}
                onMove={onMove}
                depth={depth + 1} 
              />
            ))}
          </div>
        </SortableContext>
      )}
    </div>
  );
}

export default function TagManagerPage() {
  const navigate = useNavigate();
  const { tagDictionary, saveTagDictionary, loading } = useTags();

  const isAdmin = auth.currentUser?.email === 'brett.gaddy@gmail.com';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  if (loading) return <div className="p-8 font-bold text-zinc-400">Loading Tags...</div>;

  if (!isAdmin) {
    return (
      <div className="h-full w-full bg-zinc-50 flex flex-col items-center justify-center p-8">
        <h1 className="text-2xl font-black text-zinc-900 tracking-tight mb-2">Access Denied</h1>
        <button onClick={() => navigate(-1)} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold">Go Back</button>
      </div>
    );
  }

  const roots = Object.values(tagDictionary)
    .filter(t => t.parentId === null && t.name !== 'Want To Go')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeTag = tagDictionary[active.id];
    const overTag = tagDictionary[over.id];

    if (activeTag.parentId !== overTag.parentId) {
      // Basic implementation only allows reordering within same parent to prevent massive structural mess
      return;
    }

    const siblings = Object.values(tagDictionary)
      .filter(t => t.parentId === activeTag.parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
      
    const oldIndex = siblings.findIndex(s => s.id === active.id);
    const newIndex = siblings.findIndex(s => s.id === over.id);

    const reordered = arrayMove(siblings, oldIndex, newIndex);
    
    const newDict = { ...tagDictionary };
    reordered.forEach((tag, index) => {
      newDict[tag.id] = { ...tag, sortOrder: index };
    });

    saveTagDictionary(newDict);
  };

  const handleToggleSpecial = (id) => {
    const newDict = { ...tagDictionary };
    newDict[id] = { ...newDict[id], isSpecial: !newDict[id].isSpecial };
    saveTagDictionary(newDict);
  };

  const handleRemove = (id) => {
    if (!window.confirm("Remove this tag? This will also remove all its sub-tags.")) return;
    const newDict = { ...tagDictionary };
    
    const deleteRecursively = (tagId) => {
      delete newDict[tagId];
      Object.values(newDict).forEach(t => {
        if (t.parentId === tagId) deleteRecursively(t.id);
      });
    };
    
    deleteRecursively(id);
    saveTagDictionary(newDict);
  };

  const handleAddSubTag = (parentId, name) => {
    const newDict = { ...tagDictionary };
    const trimmed = name.trim();
    if (Object.values(newDict).some(t => t.name.toLowerCase() === trimmed.toLowerCase())) {
      alert(`A tag named "${trimmed}" already exists. Please choose a different name.`);
      return;
    }
    const siblings = Object.values(newDict).filter(t => t.parentId === parentId);
    const id = `tag_custom_${Date.now()}`;
    newDict[id] = {
      id,
      name: trimmed,
      parentId,
      sortOrder: siblings.length,
      isRoot: false,
      isSpecial: false
    };
    saveTagDictionary(newDict);
  };

  const handleEdit = (tag) => {
    const newName = window.prompt("Enter new name for tag:", tag.name);
    if (newName && newName.trim() && newName.trim() !== tag.name) {
      const trimmed = newName.trim();
      const newDict = { ...tagDictionary };
      
      if (Object.values(newDict).some(t => t.name.toLowerCase() === trimmed.toLowerCase())) {
        alert(`A tag named "${trimmed}" already exists. Please choose a different name.`);
        return;
      }
      
      newDict[tag.id] = { ...tag, name: trimmed };
      saveTagDictionary(newDict);
    }
  };

  const handleMove = (tag) => {
    const parentName = window.prompt(`Move "${tag.name}" into another tag.\n\nType the EXACT NAME of the destination tag (e.g. "Food").\nLeave completely blank to make it a Root category.`);
    if (parentName === null) return; // Cancelled
    
    const newDict = { ...tagDictionary };
    let newParentId = null;
    
    if (parentName.trim() !== "") {
      const destTag = Object.values(newDict).find(t => t.name.toLowerCase() === parentName.trim().toLowerCase());
      if (!destTag) {
        alert(`Could not find a tag named "${parentName}". Please check spelling exactly.`);
        return;
      }
      
      const getDescendants = (id) => {
        let desc = [];
        const children = Object.values(newDict).filter(t => t.parentId === id);
        for (const c of children) {
          desc.push(c.id);
          desc = desc.concat(getDescendants(c.id));
        }
        return desc;
      };
      const invalidIds = new Set([tag.id, ...getDescendants(tag.id)]);
      
      if (invalidIds.has(destTag.id)) {
        alert("You cannot move a tag into itself or one of its own sub-tags.");
        return;
      }
      
      newParentId = destTag.id;
    }
    
    newDict[tag.id] = { ...tag, parentId: newParentId };
    
    // Put at bottom of new parent list
    const siblings = Object.values(newDict).filter(t => t.parentId === newParentId && t.id !== tag.id);
    newDict[tag.id].sortOrder = siblings.length;
    
    saveTagDictionary(newDict);
  };

  return (
    <div className="h-full w-full bg-zinc-50 flex flex-col overflow-y-auto custom-scrollbar">
      <div className="bg-white border-b border-zinc-200 px-6 py-4 flex flex-col gap-4 sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/database')} className="p-2 hover:bg-zinc-100 rounded-full text-zinc-500 transition-colors">
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-zinc-900 tracking-tight flex items-center gap-2">
              Tag Manager <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[9px] uppercase rounded-full">Admin</span>
            </h1>
            <p className="text-xs font-bold text-zinc-500 tracking-wide mt-1 leading-snug max-w-xl">
              Drag and drop tags to reorder them globally across the app. 
              Click the <Palmtree size={12} className="inline text-orange-500 mx-0.5" /> to mark a tag as "Special" for quick-access filtering.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl w-full mx-auto p-6 pb-24">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={roots.map(r => r.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-3">
              {roots.map(root => (
                <SortableTagItem 
                  key={root.id} 
                  tag={root} 
                  dict={tagDictionary} 
                  onToggleSpecial={handleToggleSpecial} 
                  onRemove={handleRemove} 
                  onAddSubTag={handleAddSubTag} 
                  onEdit={handleEdit}
                  onMove={handleMove}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

      </div>
    </div>
  );
}